<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  tab: {
    type: String,
    default: 'pending',
  },
});

const emit = defineEmits(['merge', 'snooze', 'ignore', 'restore']);

const { t } = useI18n();
const { accountId } = useAccount();

const suggested = computed(() =>
  props.group.contacts.find(c => c.id === props.group.suggested_base_id)
);

const others = computed(() =>
  props.group.contacts.filter(c => c.id !== props.group.suggested_base_id)
);

const totalConversations = computed(() =>
  props.group.contacts.reduce((sum, c) => sum + (c.conversations_count || 0), 0)
);

const contactUrl = id => `/app/accounts/${accountId.value}/contacts/${id}`;

const formatActivity = value => {
  if (!value) return t('CONTACTS_LAYOUT.WAHA_DUPLICATES.NO_ACTIVITY');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t('CONTACTS_LAYOUT.WAHA_DUPLICATES.NO_ACTIVITY');
  }
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};
</script>

<template>
  <article
    class="flex flex-col gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-4"
    :data-testid="`waha-dup-group-${group.waha_lid}`"
  >
    <header class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-2">
          <span class="i-ri-whatsapp-line size-4 shrink-0 text-n-slate-11" />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{
              t('CONTACTS_LAYOUT.WAHA_DUPLICATES.GROUP_TITLE', {
                count: group.contacts.length,
              })
            }}
          </h3>
          <span
            class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11 tabular-nums"
          >
            {{
              t('CONTACTS_LAYOUT.WAHA_DUPLICATES.LID_BADGE', {
                lid: group.waha_lid,
              })
            }}
          </span>
        </div>
        <p class="mt-1 text-xs text-n-slate-11">
          {{
            t('CONTACTS_LAYOUT.WAHA_DUPLICATES.GROUP_SUMMARY', {
              count: totalConversations,
            })
          }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <template v-if="tab === 'pending'">
          <button
            type="button"
            class="min-h-11 rounded-full border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="waha-dup-snooze"
            @click="emit('snooze', group)"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.ACTIONS.SNOOZE') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-full border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="waha-dup-ignore"
            @click="emit('ignore', group)"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.ACTIONS.IGNORE') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold text-n-background hover:opacity-90"
            data-testid="waha-dup-merge"
            @click="emit('merge', group)"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.ACTIONS.MERGE') }}
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="min-h-11 rounded-full border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="waha-dup-restore"
            @click="emit('restore', group)"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.ACTIONS.RESTORE') }}
          </button>
          <button
            type="button"
            class="min-h-11 rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold text-n-background hover:opacity-90"
            data-testid="waha-dup-merge"
            @click="emit('merge', group)"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.ACTIONS.MERGE') }}
          </button>
        </template>
      </div>
    </header>

    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div
        v-for="contact in group.contacts"
        :key="contact.id"
        class="flex flex-col gap-2 rounded-2xl border border-n-weak bg-n-alpha-2 p-3"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-n-slate-12">
              {{ contact.name || t('CONTACTS_LAYOUT.WAHA_DUPLICATES.NO_NAME') }}
            </div>
            <div class="truncate text-xs text-n-slate-11 tabular-nums">
              {{ contact.phone_number || '—' }}
            </div>
          </div>
          <span
            v-if="contact.id === group.suggested_base_id"
            class="shrink-0 rounded-full bg-n-blue-3 px-2 py-0.5 text-[11px] font-semibold text-n-blue-11"
          >
            {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.SUGGESTED') }}
          </span>
        </div>

        <dl class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <div class="flex flex-col">
            <dt class="text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.FIELDS.CONVERSATIONS') }}
            </dt>
            <dd class="font-medium text-n-slate-12 tabular-nums">
              {{ contact.conversations_count || 0 }}
            </dd>
          </div>
          <div class="flex flex-col">
            <dt class="text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.FIELDS.ATTRIBUTES') }}
            </dt>
            <dd class="font-medium text-n-slate-12 tabular-nums">
              {{ contact.custom_attributes_count || 0 }}
            </dd>
          </div>
          <div class="flex flex-col">
            <dt class="text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.FIELDS.LAST_ACTIVITY') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatActivity(contact.last_activity_at) }}
            </dd>
          </div>
          <div class="flex flex-col">
            <dt class="text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.FIELDS.CREATED') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatActivity(contact.created_at) }}
            </dd>
          </div>
        </dl>

        <div v-if="contact.inboxes?.length" class="flex flex-wrap gap-1">
          <span
            v-for="inbox in contact.inboxes"
            :key="inbox"
            class="rounded-full border border-n-weak px-2 py-0.5 text-[11px] text-n-slate-11"
          >
            {{ inbox }}
          </span>
        </div>

        <a
          :href="contactUrl(contact.id)"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-xs text-n-blue-11 hover:underline"
        >
          <span class="i-ri-external-link-line size-3" />
          {{ t('CONTACTS_LAYOUT.WAHA_DUPLICATES.OPEN_CONTACT') }}
        </a>
      </div>
    </div>

    <p v-if="suggested && others.length" class="text-xs text-n-slate-11">
      {{
        t('CONTACTS_LAYOUT.WAHA_DUPLICATES.SUGGESTION_HINT', {
          name: suggested.name,
        })
      }}
    </p>
  </article>
</template>
